import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { TEAM_BUDGET_PAYMENT_COPY } from '@sportninja/common/features/registration/teamBudgetPayment/constants/copy'
import type {
  TeamBudgetPaymentChoice,
  TeamBudgetSessionFields,
} from '@sportninja/common/features/registration/teamBudgetPayment/types'
import {
  classifyCheckoutRefusal,
  readSessionFieldError,
  type CheckoutRefusal,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/checkoutErrors'
import {
  centsForChoice,
  choiceFromSession,
  isCheckoutFunded,
  isPricedFor,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetSessionPricing'

const FIELD = 'team_budget_payment_cents'

/** How long the rep pauses typing an amount before it is priced and its card form appears. */
export const TYPING_PAUSE_MS = 800

/** A payment-step write the server refused, reduced to what the step shows. */
export interface TeamBudgetChoiceRefusal {
  kind: CheckoutRefusal['kind'] | 'network'
  message: string
}

interface UseTeamBudgetPaymentChoiceArgs {
  session: (TeamBudgetSessionFields & { id?: string }) | null | undefined
  /** A Team Budget rep offered to pay now, on an api2 that takes it first. */
  enabled: boolean
  /** The deposit (Minimum To Qualify), pre-tax, in cents. */
  depositCents: number | null
  /** The payment step's PUT — the context keeps the priced session it answers. */
  updateSessionPayment: (params: {
    team_budget_payment_cents: number | null
  }) => Promise<unknown>
}

const toCents = (amount: number | null): number | null =>
  amount !== null && Number.isFinite(amount) ? Math.round(amount * 100) : null

const refusalOf = (error: unknown): TeamBudgetChoiceRefusal => {
  const refusal = classifyCheckoutRefusal(error)

  if (refusal === null) {
    return { kind: 'network', message: TEAM_BUDGET_PAYMENT_COPY.retry }
  }

  const byKind: Record<CheckoutRefusal['kind'], string> = {
    retry: TEAM_BUDGET_PAYMENT_COPY.retry,
    reprice: TEAM_BUDGET_PAYMENT_COPY.retry,
    locked: TEAM_BUDGET_PAYMENT_COPY.locked,
    unpaid: TEAM_BUDGET_PAYMENT_COPY.retry,
    outdated: TEAM_BUDGET_PAYMENT_COPY.outdated,
    refused: refusal.message ?? TEAM_BUDGET_PAYMENT_COPY.retry,
  }

  return { kind: refusal.kind, message: byKind[refusal.kind] }
}

/**
 * SN-8441 / SN-8442 — the Team Budget rep's choice on the payment step, recorded on
 * the SERVER before anything is paid: the deposit (the default), another amount, or
 * Not now. The server prices the choice and opens its checkout; the card form is
 * offered only once the session is priced for exactly the choice on screen
 * (`isPriced`). This never confirms the registration — a chosen payment registers
 * the team once it goes through, and Not now is confirmed by the step's own button.
 *
 * - Every choice change is sent; a change made while one is in flight replaces the
 *   queued one, so only the latest is sent next (never a stale amount last).
 * - "A different amount" is priced once the rep stops typing (a short pause), or at
 *   once on blur or Enter — never per keystroke, and never twice: an amount the
 *   session already holds is not sent again (leaving the field for the card form
 *   must not rebuild the card form).
 * - 422 on the amount → the field's error; the controls stay usable.
 * - 409: another change in flight or no provider answer → Try Again; the price moved
 *   → sent again once; a payment already taken → locked ("don't pay again"); a page
 *   that predates the checkout → reload.
 */
export const useTeamBudgetPaymentChoice = ({
  session,
  enabled,
  depositCents,
  updateSessionPayment,
}: UseTeamBudgetPaymentChoiceArgs) => {
  'use memo'

  const initial = choiceFromSession(session, depositCents)
  const [choice, setChoiceState] = useState<TeamBudgetPaymentChoice>(
    initial.choice
  )
  const [customAmount, setCustomAmount] = useState<number | null>(
    initial.customCents !== null ? initial.customCents / 100 : null
  )
  const [typedAmount, setTypedAmount] = useState<number | null>(customAmount)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<TeamBudgetChoiceRefusal | null>(null)
  const [isCommitting, setIsCommitting] = useState(false)

  // Synchronous queue state: a choice made before the next render must not be lost.
  const inFlight = useRef(false)
  const queued = useRef<{ cents: number | null } | null>(null)
  const lastSent = useRef<number | null | undefined>(undefined)
  const enteredWith = useRef(false)
  // The amount typed, waiting for the rep to stop typing before it is priced.
  const pendingPrice = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The session as last rendered, for the handlers and a price that fires after a pause.
  const latestSession = useRef(session)

  // Synced in the commit itself: a click right after a render must read that render's session.
  useLayoutEffect(() => {
    latestSession.current = session
  })

  // An amount still waiting when the step goes away is never sent.
  useEffect(
    () => () => {
      if (pendingPrice.current !== null) {
        clearTimeout(pendingPrice.current)
      }
    },
    []
  )

  // The choice on screen: for "a different amount", what is typed in the field.
  const chosenCents = centsForChoice({
    choice,
    customCents: toCents(typedAmount),
    depositCents,
  })

  const send = async (cents: number | null) => {
    queued.current = { cents }

    if (inFlight.current) {
      return
    }

    inFlight.current = true
    setIsCommitting(true)

    try {
      let repriced = false

      while (queued.current !== null) {
        const next: number | null = queued.current.cents
        queued.current = null
        lastSent.current = next

        try {
          await updateSessionPayment({ team_budget_payment_cents: next })
          setFieldError(null)
          setRefusal(null)
        } catch (error) {
          if (queued.current !== null) {
            // A newer choice supersedes this answer.
            continue
          }

          const message = readSessionFieldError(error, FIELD)
          if (message !== null) {
            setFieldError(message)
            setRefusal(null)
            continue
          }

          const reason = refusalOf(error)
          if (reason.kind === 'reprice' && !repriced) {
            // The amount due moved since it was quoted: price it again, once.
            repriced = true
            queued.current = { cents: next }
            continue
          }

          setRefusal(reason)
        }
      }
    } finally {
      inFlight.current = false
      setIsCommitting(false)
    }
  }

  // Entering the step: record the choice on screen unless the session is priced for it.
  useEffect(() => {
    if (!enabled || enteredWith.current || !session?.id) {
      return
    }
    enteredWith.current = true

    if (
      chosenCents !== undefined &&
      !isPricedFor(session, chosenCents) &&
      !isCheckoutFunded(session)
    ) {
      send(chosenCents)
    }
  })

  const cancelPendingPrice = () => {
    if (pendingPrice.current !== null) {
      clearTimeout(pendingPrice.current)
      pendingPrice.current = null
    }
  }

  /**
   * Whether the session holds this choice already: priced for it — or, while a
   * request is out, about to be (it is the latest one asked for). Only then may a
   * request be skipped; otherwise an older answer would leave the choice on screen
   * unpriced.
   */
  const holdsAlready = (cents: number | null): boolean => {
    if (inFlight.current) {
      const latest =
        queued.current !== null ? queued.current.cents : lastSent.current
      return cents === latest
    }
    return isPricedFor(latestSession.current, cents)
  }

  /** Prices a typed amount — unless the session already holds it. */
  const priceAmount = (cents: number) => {
    if (holdsAlready(cents)) {
      return
    }
    setFieldError(null)
    send(cents)
  }

  const setChoice = (next: TeamBudgetPaymentChoice) => {
    cancelPendingPrice()
    setChoiceState(next)
    setFieldError(null)

    const cents = centsForChoice({
      choice: next,
      customCents: toCents(typedAmount),
      depositCents,
    })

    // A choice the session still holds needs no request: its card form is back at once.
    if (cents !== undefined && !holdsAlready(cents)) {
      send(cents)
    }
  }

  /** Typing in the amount field: priced once the rep pauses. */
  const onCustomAmountTyped = (amount: number | null) => {
    setTypedAmount(amount)
    setFieldError(null)
    cancelPendingPrice()

    const cents = toCents(amount)
    if (choice === 'custom' && cents !== null) {
      pendingPrice.current = setTimeout(() => {
        pendingPrice.current = null
        priceAmount(cents)
      }, TYPING_PAUSE_MS)
    }
  }

  /** The amount field committed (blur): priced at once, when it is new. */
  const commitCustomAmount = (amount: number | null) => {
    cancelPendingPrice()
    const committed = amount !== null && Number.isFinite(amount) ? amount : null
    setCustomAmount(committed)
    setTypedAmount(committed)

    const cents = toCents(committed)
    if (choice === 'custom' && cents !== null) {
      priceAmount(cents)
    }
  }

  /** Enter: commit what is typed — it never pays. */
  const commitTyped = () => {
    if (choice === 'custom') {
      commitCustomAmount(typedAmount)
    }
  }

  /**
   * The choice on screen, sent again: Try Again after a refusal — or, once a declined
   * card has ended the checkout it was priced with, a new checkout for the same choice
   * (never skipped as "already held": the session on screen is out of date).
   */
  const retry = () => {
    const cents =
      lastSent.current !== undefined ? lastSent.current : chosenCents
    if (cents !== undefined) {
      setRefusal(null)
      send(cents)
    }
  }

  const isLocked = isCheckoutFunded(session) || refusal?.kind === 'locked'

  return {
    choice,
    setChoice,
    customAmount,
    typedAmount,
    onCustomAmountTyped,
    commitCustomAmount,
    commitTyped,
    chosenCents,
    fieldError,
    refusal,
    retry,
    isCommitting,
    isLocked,
    /** The session is priced for exactly this choice (and, to pay, its checkout is open). */
    isPriced: !isCommitting && !isLocked && isPricedFor(session, chosenCents),
  }
}
